import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined'
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import {
    useIntegrationsAndApiData,
    hasAnyIntegrationActive
} from './hooks/use-integrations-and-api-data'

const useStyles = makeStyles(theme => ({
    card: {
        border: theme.border,
        borderRadius: theme.borderRadius,
        padding: 24,
        marginBottom: 24,
        background: theme.background
    },
    title: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        marginBottom: 20
    },
    body: {
        display: 'flex',
        gap: 32,
        flexWrap: 'wrap',
        alignItems: 'stretch'
    },
    metricsRow: {
        display: 'flex',
        gap: 16
    },
    metricTile: {
        minWidth: 140,
        padding: '14px 18px',
        border: theme.border,
        borderRadius: theme.borderRadius,
        background: theme.lightBlueGrey,
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    metricHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: theme.textSecondary,
        fontSize: 12,
        fontFamily: theme.fontFamily,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    },
    metricIcon: {
        fontSize: 16
    },
    metricValue: {
        fontSize: 26,
        fontWeight: 'bold',
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        lineHeight: 1
    },
    metricSub: {
        fontSize: 11,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        marginTop: 2
    },
    chipsColumn: {
        flex: 1,
        minWidth: 240,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    chipsLabel: {
        fontSize: 12,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    },
    chipsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: theme.fontFamily,
        border: theme.border
    },
    chipOn: {
        background: theme.greenLight,
        color: theme.green,
        borderColor: theme.greenLight
    },
    chipOff: {
        background: theme.grayLight,
        color: theme.gray,
        borderColor: theme.grayLight
    },
    chipDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: 'currentColor'
    },
    billingRow: {
        marginTop: 20,
        paddingTop: 20,
        borderTop: theme.border,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start'
    },
    billingIcon: {
        color: theme.blue,
        flexShrink: 0,
        marginTop: 2
    },
    billingTextWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6
    },
    billingNotice: {
        fontSize: 13,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        lineHeight: 1.5
    },
    billingLink: {
        background: 'none',
        border: 'none',
        padding: 0,
        color: theme.primary,
        fontSize: 13,
        fontWeight: 'bold',
        fontFamily: theme.fontFamily,
        textDecoration: 'underline',
        cursor: 'pointer',
        textAlign: 'left',
        '&:hover': {
            opacity: 0.6
        }
    },
    placeholder: {
        height: 110,
        background: theme.lightBlueGrey,
        border: theme.border,
        borderRadius: theme.borderRadius,
        marginBottom: 24
    }
}))

const INTEGRATION_KEYS = ['pestscan', 'pestsoft', 'debuxx']

function StatusChip({ enabled, label, classes }) {
    return (
        <span
            className={`${classes.chip} ${
                enabled ? classes.chipOn : classes.chipOff
            }`}
        >
            <span className={classes.chipDot} />
            {label}
        </span>
    )
}

export default function IntegrationsSummary() {
    const classes = useStyles()
    const locale = useSelector(state => state.main.locale)
    const history = useHistory()
    const { data, isLoading } = useIntegrationsAndApiData()

    if (isLoading) {
        return <div className={classes.placeholder} />
    }
    if (!data) {
        return null
    }

    const settings = data.external_integration_settings ?? {}
    const showBilling = hasAnyIntegrationActive(data)

    return (
        <div className={classes.card}>
            <Typography className={classes.title}>
                {locale.integrations.summaryTitle}
            </Typography>

            <div className={classes.body}>
                <div className={classes.metricsRow}>
                    <div className={classes.metricTile}>
                        <div className={classes.metricHead}>
                            <VpnKeyOutlinedIcon
                                className={classes.metricIcon}
                            />
                            {locale.integrations.apiKeysCount}
                        </div>
                        <div className={classes.metricValue}>
                            {data.api_keys_amount ?? 0}
                        </div>
                    </div>
                    <div className={classes.metricTile}>
                        <div className={classes.metricHead}>
                            <SettingsEthernetIcon
                                className={classes.metricIcon}
                            />
                            {locale.integrations.webhooks}
                        </div>
                        <div className={classes.metricValue}>
                            {data.webhooks_amount ?? 0}
                        </div>
                    </div>
                </div>

                <div className={classes.chipsColumn}>
                    <span className={classes.chipsLabel}>
                        {locale.integrations.integrations}
                    </span>
                    <div className={classes.chipsRow}>
                        {INTEGRATION_KEYS.map(key => (
                            <StatusChip
                                key={key}
                                enabled={settings[key] === true}
                                label={locale.integrations[key]}
                                classes={classes}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {showBilling && (
                <div className={classes.billingRow}>
                    <InfoOutlinedIcon className={classes.billingIcon} />
                    <div className={classes.billingTextWrap}>
                        <span className={classes.billingNotice}>
                            {locale.integrations.billingNotice}
                        </span>
                        <button
                            type="button"
                            className={classes.billingLink}
                            onClick={() => history.push('/billing')}
                        >
                            {locale.integrations.openBillingDashboard}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
